import { createElement } from 'react'
import type { Step } from 'react-joyride'

import { FtuxStepHeader } from '@/features/dashboard/lib/ftux-step-title'

/** Stable anchors shared with tests — align copy with user story + Figma (`4154:4366` …, editor tours `4154:4389` … `4154:4458`). */
export const FTUX_TARGETS = {
  adminMetricCampaigns: '[data-testid="sensemaker-ftux-metric-campaigns"]',
  adminMetricProviders: '[data-testid="sensemaker-ftux-metric-providers"]',
  adminMetricIncentives: '[data-testid="sensemaker-ftux-metric-incentives"]',
  adminCreateCampaign: '[data-testid="sensemaker-ftux-create-campaign"]',
  notifications: '[data-testid="sensemaker-ftux-notifications"]',
  profile: '[data-testid="sensemaker-ftux-profile"]',
  /**
   * Tour 1 — zero-width anchor at trailing edge of “Allocated Instruments” for tooltip placement.
   * Spotlight uses `evInstrumentList` via `spotlightTarget` in `editorViewerFtuxSteps`.
   */
  evTour1Anchor: '[data-testid="sensemaker-ftux-ev-tour1-anchor"]',
  evInstrumentList: '[data-testid="sensemaker-ftux-ev-instrument-list"]',
  evStatusFilter: '[data-testid="sensemaker-ftux-ev-status-filter"]',
} as const

const ADMIN_FTUX_TOTAL = 6
const EDITOR_FTUX_TOTAL = 4
/** Gap between “Allocated Instruments” title end and tour beacon. */
export const FTUX_EV_TOUR1_GAP_AFTER_TITLE_PX = 5
/** Instrument List tooltip fine-tune — slight nudge from Joyride `top-start` anchor. */
const FTUX_EV_TOUR1_TOOLTIP_NUDGE_X_PX = 5
const FTUX_EV_TOUR1_TOOLTIP_NUDGE_Y_PX = 14

const evTour1TooltipNudgeMiddleware = {
  name: 'evTour1TooltipNudge',
  fn({ x, y }: { x: number; y: number }) {
    return {
      x: x + FTUX_EV_TOUR1_TOOLTIP_NUDGE_X_PX,
      y: y + FTUX_EV_TOUR1_TOOLTIP_NUDGE_Y_PX,
    }
  },
} as const

function ftuxTitle(title: string, stepIndex: number, total: number): Step['title'] {
  return createElement(FtuxStepHeader, { title, stepIndex, total })
}

/**
 * AppShell uses a nested scroll container — keep Joyride floater/spotlight in viewport
 * space (`fixed`) so cutouts don’t drift after step-to-step scrolling.
 */
const FTUX_VIEWPORT_FIXED = { isFixed: true as const }

export function adminFtuxSteps(): Step[] {
  return [
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.adminMetricCampaigns,
      title: ftuxTitle('Total Active Campaign', 1, ADMIN_FTUX_TOTAL),
      content:
        'Shows the number of currently running campaigns across the platform in real time',
      placement: 'bottom',
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.adminMetricProviders,
      title: ftuxTitle('Total Verified Signal Providers', 2, ADMIN_FTUX_TOTAL),
      content:
        'Shows the total number of verified Signal Providers available to respond to instruments.',
      placement: 'bottom',
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.adminMetricIncentives,
      title: ftuxTitle('Total Incentives Distributed', 3, ADMIN_FTUX_TOTAL),
      content:
        'Tracks the total rewards distributed to Signal Providers across all campaigns.',
      placement: 'bottom',
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.adminCreateCampaign,
      title: ftuxTitle('Create New Campaign', 4, ADMIN_FTUX_TOTAL),
      content: 'Clicking here starts the campaign creation flow step by step.',
      placement: 'bottom',
      /** Match MUI create button `borderRadius: 8px` (metric cards keep default 10). */
      spotlightRadius: 8,
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.notifications,
      title: ftuxTitle('Notifications', 5, ADMIN_FTUX_TOTAL),
      content:
        'Keeps admin updated on campaign activity, new responses, and platform alerts.',
      placement: 'bottom',
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.profile,
      title: ftuxTitle('Profile icon & Settings', 6, ADMIN_FTUX_TOTAL),
      content: 'Provides access to account settings and platform preferences.',
      placement: 'right',
    },
  ]
}

export function editorViewerFtuxSteps(): Step[] {
  return [
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.evTour1Anchor,
      spotlightTarget: FTUX_TARGETS.evInstrumentList,
      skipScroll: true,
      beaconPlacement: 'right',
      title: ftuxTitle('Instrument List', 1, EDITOR_FTUX_TOTAL),
      content: 'Shows all instruments allocated to this user based on their role',
      placement: 'top-start',
      offset: 10,
      floatingOptions: {
        beaconOptions: { offset: FTUX_EV_TOUR1_GAP_AFTER_TITLE_PX },
        flipOptions: false,
        middleware: [evTour1TooltipNudgeMiddleware],
      },
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.evStatusFilter,
      title: ftuxTitle('Status Filter', 2, EDITOR_FTUX_TOTAL),
      content:
        'Allows filtering allocated instruments by Live, Paused, Completed, or Draft status',
      placement: 'bottom',
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.notifications,
      title: ftuxTitle('Notification Icon', 3, EDITOR_FTUX_TOTAL),
      content:
        'Keeps the user updated on instrument activity, new assignments, and platform alerts',
      placement: 'bottom',
    },
    {
      ...FTUX_VIEWPORT_FIXED,
      target: FTUX_TARGETS.profile,
      title: ftuxTitle('Profile Icon & Settings', 4, EDITOR_FTUX_TOTAL),
      content: 'Provides access to account settings and platform preferences',
      placement: 'right',
    },
  ]
}
